import { useUser } from '@trustpad/launchpad';
import { StakingContractV2 } from '@trustpad/launchpad/dist/staking/hooks/useStakingContract';
import { StakingState } from '@trustpad/launchpad/dist/staking/hooks/useStakingV2';

import BN from 'bn.js';
import * as React from 'react';
import { useState } from 'react';
import { FaLevelUpAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { levelPools } from '~config/levelPoolsConfig';
import { ActionButton } from '~features/ui';

export function UpgradeBlock({
  userStats,
  refreshAll,
  stakingContract,
  currentStaking,
}: {
  userStats: { unlocksAt: number; amount: BN };
  refreshAll: VoidFunction;
  stakingContract: StakingContractV2;
  currentStaking: StakingState;
}) {
  const { account } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const poolsForUpgrade = levelPools.filter(
    (p) => p.lockPeriodSeconds > currentStaking.lockPeriod,
  );
  const [targetPool, setTargetPool] = useState(
    poolsForUpgrade[poolsForUpgrade.length - 1]?.address || '',
  );

  function upgrade() {
    setIsLoading(true);
    stakingContract.methods
      .upPool(targetPool)
      .send({ from: account })
      .then(() => {
        refreshAll();
        toast.success(`Moved tokens to the higher staking pool.`, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .catch((e) => {
        toast.error(`There was a problem upping the staking pool.`, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .finally(() => setIsLoading(false));
  }

  if (!account || userStats.amount.eqn(0) || !poolsForUpgrade.length) {
    return null;
  }

  return (
    <div className="pt-6 flex gap-3">
      <div className="space-y-3">
        <h4>Upgrade staking pool</h4>
        <ul className="list-disc list-inside text-sm">
          <li>
            Moves all tokens from the current pool to the selected one{' '}
            <b>without fee</b>
          </li>
          <li>
            <b>Restarts the lock</b> on the selected pool
          </li>
          <li>Keeps the earned rewards intact in the old pool</li>
        </ul>
      </div>
      <div className="flex-shrink-0 flex flex-col items-end gap-3">
        <div className="text-center">
          <label>Target pool</label>

          <div className="space-x-3">
            <select
              className="px-3 py-2 bg-dark2 rounded focus:outline-mainDark focus:border-0"
              value={targetPool}
              onChange={(e) => {
                setTargetPool(e.currentTarget.value);
              }}>
              {poolsForUpgrade.map((p) => (
                <option key={p.id} value={p.address}>
                  {p.lockPeriod}
                </option>
              ))}
            </select>
          </div>
        </div>

        <ActionButton
          isLoading={isLoading}
          isDisabled={isLoading}
          onClick={() => upgrade()}
          className="w-28 whitespace-nowrap">
          Upgrade <FaLevelUpAlt className="inline" />
        </ActionButton>
      </div>
    </div>
  );
}
